import React, { useState, useEffect, useCallback } from 'react';
import { Truck, Upload, Package, Copy, ExternalLink, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import api from '../../lib/api';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_POINT_LABEL = {
  mondial_relay: 'Mondial Relay',
  colissimo: 'Colissimo',
  colis_prive: 'Colis Privé',
};

const AdminLabelsTab = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState({});
  const [trackingInputs, setTrackingInputs] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/orders/awaiting-label');
      setOrders(res.data.orders || []);
    } catch (e) {
      toast.error("Erreur lors du chargement des commandes");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleUpload = async (orderId, file) => {
    if (!file) return;
    if (file.type !== 'application/pdf') {
      toast.error("Le fichier doit être un PDF");
      return;
    }
    setUploading(prev => ({ ...prev, [orderId]: true }));
    try {
      const fd = new FormData();
      fd.append('file', file);
      const tracking = (trackingInputs[orderId] || '').trim();
      if (tracking) fd.append('tracking_number', tracking);
      const res = await api.post(
        `/admin/orders/${orderId}/upload-label`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      toast.success(`Étiquette uploadée — tracking ${res.data.tracking_number}`);
      load();
    } catch (e) {
      const detail = e.response?.data?.detail || "Erreur lors de l'upload";
      toast.error(detail);
    } finally {
      setUploading(prev => ({ ...prev, [orderId]: false }));
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copié !`);
  };

  return (
    <div data-testid="admin-labels">
      <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-xl">
        <h3 className="font-bold text-[#033871] flex items-center gap-2 mb-1">
          <Truck className="w-4 h-4" /> Étiquettes d&apos;expédition à préparer
        </h3>
        <p className="text-xs text-[#5A8BA8]">
          Commandes payées en attente de votre étiquette PDF. Générez-la sur Boxtal/Sendcloud/Colissimo (ou réservez sur Eurosender pour les vendeurs hors France) puis uploadez-la ici. Le vendeur recevra un email automatique.
        </p>
      </div>

      {loading && <p className="text-center text-sm text-[#5A8BA8] py-6">Chargement…</p>}

      {!loading && orders.length === 0 && (
        <div className="text-center py-12">
          <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-3" />
          <p className="text-sm text-[#5A8BA8]">Aucune commande en attente d&apos;étiquette.</p>
        </div>
      )}

      <div className="space-y-3">
        {orders.map(o => {
          const seller = o.seller_address || {};
          const shipping = o.shipping || {};
          const isUploading = uploading[o.id];
          const isEurosender = o.shipping_method_id === 'eurosender_standard';
          return (
            <div key={o.id} className="bg-white rounded-xl border border-[#E8F4FD] p-4" data-testid={`label-row-${o.id}`}>
              {/* Header */}
              <div className="flex items-start justify-between flex-wrap gap-2 mb-3">
                <div className="min-w-0">
                  <p className="font-bold text-[#033871]">{o.brand} {o.model}</p>
                  <p className="text-[11px] text-[#5A8BA8]">
                    #{o.id.slice(0, 8)} • {new Date(o.created_at).toLocaleDateString('fr-FR')}
                  </p>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  {isEurosender && (
                    <span className="px-2 py-1 bg-amber-100 text-amber-800 rounded-full font-bold" data-testid={`eurosender-badge-${o.id}`}>
                      À réserver sur Eurosender • {seller.country || '?'} → {shipping.country || 'FR'} • coût ≈ {(o.shipping_cost - 1).toFixed(2)}€
                    </span>
                  )}
                  <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full font-medium">
                    {o.shipping_method_name || 'Livraison'}
                  </span>
                  <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full font-medium">
                    Payée • {o.total?.toFixed(2)}€
                  </span>
                </div>
              </div>

              {/* Addresses grid */}
              <div className="grid md:grid-cols-2 gap-3 mb-3 text-xs">
                <div className="bg-[#F7FAFB] rounded-lg p-3 border border-[#E8F4FD]">
                  <p className="font-bold text-[#033871] mb-1 flex items-center gap-1.5">
                    <Package className="w-3 h-3" /> Expéditeur (vendeur)
                  </p>
                  <p className="text-[#5A8BA8]">{seller.first_name} {seller.last_name}</p>
                  <p className="text-[#5A8BA8]">{seller.address}</p>
                  <p className="text-[#5A8BA8]">{seller.postal_code} {seller.city} {seller.country && `(${seller.country})`}</p>
                  <p className="text-[#5A8BA8]">{seller.phone}</p>
                  <p className="text-[#5A8BA8]">{seller.email}</p>
                  <button
                    onClick={() => copyToClipboard(
                      `${seller.first_name} ${seller.last_name}\n${seller.address}\n${seller.postal_code} ${seller.city}\n${seller.phone}`,
                      'Expéditeur'
                    )}
                    className="mt-2 text-[10px] text-[#0C63BF] hover:underline inline-flex items-center gap-1"
                  >
                    <Copy className="w-3 h-3" /> Copier
                  </button>
                </div>

                <div className="bg-[#F7FAFB] rounded-lg p-3 border border-[#E8F4FD]">
                  <p className="font-bold text-[#033871] mb-1 flex items-center gap-1.5">
                    <Truck className="w-3 h-3" /> Destinataire (acheteur)
                  </p>
                  <p className="text-[#5A8BA8]">{shipping.name}</p>
                  <p className="text-[#5A8BA8]">{shipping.address}</p>
                  <p className="text-[#5A8BA8]">{shipping.postal_code} {shipping.city} {shipping.country && `(${shipping.country})`}</p>
                  <p className="text-[#5A8BA8]">{shipping.phone}</p>
                  {shipping.service_point_id && (
                    <p className="text-amber-700 font-medium mt-1">
                      ⚠️ Point relais {SERVICE_POINT_LABEL[shipping.service_point_carrier] || ''} #{shipping.service_point_id}
                    </p>
                  )}
                  <button
                    onClick={() => copyToClipboard(
                      `${shipping.name}\n${shipping.address}\n${shipping.postal_code} ${shipping.city}\n${shipping.phone}`,
                      'Destinataire'
                    )}
                    className="mt-2 text-[10px] text-[#0C63BF] hover:underline inline-flex items-center gap-1"
                  >
                    <Copy className="w-3 h-3" /> Copier
                  </button>
                </div>
              </div>

              {/* Upload row */}
              <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-[#F0F7FA]">
                <Input
                  type="text"
                  placeholder="N° de suivi (optionnel)"
                  value={trackingInputs[o.id] || ''}
                  onChange={(e) => setTrackingInputs(prev => ({ ...prev, [o.id]: e.target.value }))}
                  className="h-9 text-xs flex-1 min-w-[180px]"
                  data-testid={`tracking-input-${o.id}`}
                />
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept="application/pdf"
                    className="hidden"
                    onChange={(e) => handleUpload(o.id, e.target.files?.[0])}
                    disabled={isUploading}
                    data-testid={`upload-input-${o.id}`}
                  />
                  <span className="inline-flex items-center gap-1.5 h-9 px-4 bg-[#32CD32] hover:bg-[#28a428] text-white rounded-lg text-xs font-bold transition-colors">
                    <Upload className="w-3.5 h-3.5" />
                    {isUploading ? 'Upload…' : 'Uploader PDF'}
                  </span>
                </label>
                <a
                  href={isEurosender ? 'https://www.eurosender.com' : 'https://app.boxtal.com'}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 h-9 px-3 border border-[#0C63BF] text-[#0C63BF] hover:bg-[#E8F4FD] rounded-lg text-xs font-medium"
                  title="Ouvrir le site pour réserver le transport"
                  data-testid={`booking-link-${o.id}`}
                >
                  <ExternalLink className="w-3 h-3" /> {isEurosender ? 'Eurosender' : 'Boxtal'}
                </a>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AdminLabelsTab;
